import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_SLIDE_TEMPLATES_QUERY_KEY } from '@/state/queries/slide-templates/use-get-slide-templates';
import type { SlideTemplateDto } from '@/state/queries/slide-templates/use-get-slide-templates';

export interface UpdateSlideTemplateDto {
  name?: string;
  slug?: string;
  context?: 'event' | 'flow' | 'session' | 'slot';
  type?: 'shared' | 'organization' | 'event';
  templateJson?: {
    version: number;
    layers: Array<{
      order: number;
      componentId: string;
      position: { x: number; y: number; w: number; h: number };
      dataBindings: Record<string, string>;
      style?: Record<string, unknown>;
      remotelyToggleable?: boolean;
      overlaySlug?: string;
    }>;
    layoutRules?: Record<string, unknown>;
  };
  selectionCriteria?: {
    playerTypes?: string[];
    orientation?: 'landscape' | 'portrait';
  };
  organizationId?: string;
  eventId?: string;
}

export const useUpdateSlideTemplateMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSlideTemplateDto }) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/slide-templates/${id}`;
      return sendJSONRequest({
        url,
        method: METHOD.PATCH,
        body: data,
        token: token ?? undefined,
      }) as Promise<SlideTemplateDto>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_SLIDE_TEMPLATES_QUERY_KEY] });
    },
  });
};
